import React, { useState } from 'react';
import { Clock, Plus, Minus, X, Play, Pause } from 'lucide-react';
import { useSleepTimer } from '../hooks/useSleepTimer';
import { trackSleepTimerStart, trackSleepTimerCancel, trackSleepTimerComplete } from '../analytics';

interface SleepTimerProps {
  isVisible: boolean;
  onClose: () => void;
  onTimerEnd: () => void;
  isPlaying: boolean;
}

export default function SleepTimer({ isVisible, onClose, onTimerEnd, isPlaying }: SleepTimerProps) {
  const [customMinutes, setCustomMinutes] = useState(30);
  const [showCustomInput, setShowCustomInput] = useState(false);

  const {
    isActive,
    remainingTime,
    presetTimes,
    startTimer,
    stopTimer,
    addTime,
    formatTime,
    getProgress,
  } = useSleepTimer({
    onTimerEnd: () => {
      trackSleepTimerComplete();
      onTimerEnd();
      onClose();
    },
  });

  if (!isVisible) return null;

  const handlePresetClick = (minutes: number) => {
    trackSleepTimerStart(minutes, false);
    startTimer(minutes);
  };

  const handleCustomTimer = () => {
    if (customMinutes > 0) {
      trackSleepTimerStart(customMinutes, true);
      startTimer(customMinutes);
      setShowCustomInput(false);
    }
  };

  const adjustCustomMinutes = (delta: number) => {
    setCustomMinutes(prev => Math.max(1, Math.min(480, prev + delta))); // 1 min to 8 hours
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-black/30 bg-white/20 rounded-[2rem] border border-white/10 p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-brand-pink" />
            <h2 className="text-xl font-semibold text-white">Sleep Timer</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {!isPlaying && (
          <div className="mb-6 p-4 bg-white/5 rounded-lg">
            <div className="flex items-center gap-2 text-white/80">
              <Pause className="w-4 h-4" />
              <span className="text-sm">Start playing to use sleep timer</span>
            </div>
          </div>
        )}

        {isActive ? (
          <div className="text-center mb-6">
            <div className="relative w-32 h-32 mx-auto mb-4">
              <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  className="text-white/20"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 45}`}
                  strokeDashoffset={`${2 * Math.PI * 45 * (1 - getProgress() / 100)}`}
                  className="text-brand-pink transition-all duration-1000"
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {formatTime(remainingTime)}
                </span>
              </div>
            </div>

            <p className="text-white/80 mb-6">
              Music will stop when timer reaches zero
            </p>

            <div className="flex justify-center gap-4 mb-6">
              <button
                onClick={() => addTime(5)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>+5 min</span>
              </button>
              <button
                onClick={() => addTime(15)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>+15 min</span>
              </button>
            </div>

            <button
              onClick={() => {
                trackSleepTimerCancel();
                stopTimer();
              }}
              className="w-full py-3 bg-brand-pink rounded-lg hover:bg-brand-pink/80 transition-colors"
            >
              Cancel Timer
            </button>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {presetTimes.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => handlePresetClick(preset.value / 60)}
                  disabled={!isPlaying}
                  className="p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="text-white">{preset.label}</span>
                </button>
              ))}
            </div>

            {showCustomInput ? (
              <div className="bg-white/5 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <button
                    onClick={() => adjustCustomMinutes(-5)}
                    className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">{customMinutes}</div>
                    <div className="text-sm text-white/80">minutes</div>
                  </div>
                  <button
                    onClick={() => adjustCustomMinutes(5)}
                    className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCustomInput(false)}
                    className="flex-1 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCustomTimer}
                    disabled={!isPlaying}
                    className="flex-1 py-2 bg-brand-pink rounded-lg hover:bg-brand-pink/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Start Timer
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowCustomInput(true)}
                className="w-full py-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors mb-4"
              >
                Custom Time
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}